import {
  tankTone,
  shownScore,
  tankPill,
  bandCounts,
  summaryHeadline,
  freshnessLabel,
  statusFromEntry,
  tankAgeLabel,
  phaseLabel,
  livestockSummary,
  dosingLabel,
} from '../tank-overview.js';
import { Sparkline } from './Sparkline.jsx';
import { QuarantineBadge } from './QuarantineBadge.jsx';
import { getDisplayUnits, formatVolume, formatTemp, tempLabel } from '../units.js';
import { EmailChangeNote } from './EmailChangeNote.jsx';
import { isQuarantineTank } from '../../../shared/tank-purpose.js';
import { isFreshwaterTank } from '../first-run.js';

// Multi-tank home — the landing for keepers with 2+ tanks. At-a-glance
// by design: every tank's score ring + the four daily reefer numbers
// (temp / pH / alk / nitrate) with a trend line under each, visible
// the moment they open the app. Tap a card to drill into that tank's
// full Home; the Home nav tab brings them back here.
//
// Card anatomy (mirrors the approved mockup):
//   - score ring (band-coloured arc + number) top-left
//   - name + subtitle (volume · type) + Good/Watch status pill
//   - four param tiles: LABEL / value+unit / sparkline
//
// Header carries an aggregate: the tank that most needs a look + a
// GOOD / WATCH tally so the keeper triages before tapping anything.

// The four at-a-glance params + their display unit. Mirror of
// GRID_SPARK_PARAMS in server/routes/tanks.js (the params the server
// ships spark series for).
const STRIP_PARAMS = [
  { key: 'temp', label: 'TEMP', unit: '' },
  { key: 'ph', label: 'PH', unit: '' },
  { key: 'alk', label: 'ALK', unit: 'dKH' },
  { key: 'nitrate', label: 'NO₃', unit: 'ppm' },
];
// A quarantine tank's daily tests replace the reef pair, and so do a
// cycling tank's: ammonia and nitrite are what a new tank is tested for.
const QT_STRIP_PARAMS = [
  STRIP_PARAMS[0],
  STRIP_PARAMS[1],
  { key: 'ammonia', label: 'NH₃', unit: 'ppm' },
  { key: 'nitrite', label: 'NO₂', unit: 'ppm' },
];
// A freshwater tank has no alkalinity card, so its tile read "ALK —" beside
// the others (feedback #74): ammonia in its place.
const FRESH_STRIP_PARAMS = [STRIP_PARAMS[0], STRIP_PARAMS[1], STRIP_PARAMS[3], QT_STRIP_PARAMS[2]];

export function stripParamsFor(tank) {
  if (isQuarantineTank(tank) || tank?.phase === 'cycling') return QT_STRIP_PARAMS;
  return isFreshwaterTank(tank) ? FRESH_STRIP_PARAMS : STRIP_PARAMS;
}

export function TankOverviewGrid({ tanks, onPickTank, onAddTank, onOpenAccount, user }) {
  const list = Array.isArray(tanks) ? tanks : [];
  const counts = bandCounts(list);
  const summary = summaryHeadline(list);
  return (
    <div style={{ padding: '14px 16px 28px' }}>
      <OverviewHeader
        count={list.length}
        summary={summary}
        counts={counts}
        onOpenAccount={onOpenAccount}
      />
      <EmailChangeNote user={user} onOpenAccount={onOpenAccount} style={{ marginBottom: 12 }} />
      <div
        style={{
          display: 'grid',
          // min(): two 340 px columns' floor is wider than a 360 px phone.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))',
          gap: 14,
        }}
      >
        {list.map((t) => (
          <TankCard key={t.id} tank={t} onPick={() => onPickTank?.(t.id)} />
        ))}
        {onAddTank && (
          <button
            type="button"
            onClick={onAddTank}
            style={{
              minHeight: 188,
              borderRadius: 16,
              border: '1.5px dashed var(--surface-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontSize: 15,
              letterSpacing: '0.04em',
            }}
          >
            <span style={{ fontSize: 26.5, lineHeight: 1 }}>+</span>
            <span>Add a tank</span>
          </button>
        )}
      </div>
    </div>
  );
}

function OverviewHeader({ count, summary, counts, onOpenAccount }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        flexWrap: 'wrap',
        marginBottom: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2
            className="font-heading"
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            All tanks
          </h2>
          <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>
            {count} {count === 1 ? 'tank' : 'tanks'}
          </span>
        </div>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 15,
            color: 'var(--text-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
        {counts.good > 0 && <CountStat n={counts.good} label="Good" color="var(--ok, #6cc18a)" />}
        {counts.watch > 0 && (
          <CountStat n={counts.watch} label="Watch" color="var(--warn, #d6a754)" />
        )}
        {counts.attention > 0 && (
          <CountStat n={counts.attention} label="Attention" color="var(--alert, #d97070)" />
        )}
        {/* The grid has no top Header, so this is the keeper's only path
            to account settings on mobile. Hidden on desktop (the SideNav
            owns the Account button there) so it isn't a duplicate. */}
        {onOpenAccount && (
          <button
            type="button"
            onClick={onOpenAccount}
            aria-label="Account settings"
            title="Account settings"
            className="ov-account hdr-gear"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function CountStat({ n, label, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{n}</span>
      <span
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
        }}
      >
        {label}
      </span>
    </div>
  );
}

// "Last reading 2 h ago", or "No readings yet" on a tank with none (a new
// QT read "Last reading no readings yet").
export function lastReadingLine(ts, now = Date.now()) {
  const fresh = freshnessLabel(ts, now);
  return fresh === 'no readings yet' ? 'No readings yet' : `Last reading ${fresh}`;
}

function TankCard({ tank, onPick }) {
  const breakdown = tank.parameter_breakdown || {};
  const pill = tankPill(tank);
  const sharedBy = tank.role && tank.role !== 'owner' ? tank.role : null;
  const subtitle = tankSubtitle(tank);
  const residents = livestockSummary(tank.livestock);
  const dosing = dosingLabel(tank.dosing);

  return (
    <button
      type="button"
      onClick={onPick}
      style={{
        textAlign: 'left',
        padding: '16px 16px 14px',
        border: '1px solid var(--surface-border)',
        borderRadius: 16,
        background: 'var(--surface)',
        boxShadow: 'var(--glass-shadow)',
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* Header: ring + name/subtitle + pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <ScoreRing score={shownScore(tank)} color={tankTone(tank).color} />
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div
            className="font-heading"
            style={{
              fontSize: 19,
              fontWeight: 600,
              color: 'var(--text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {tank.name}
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-muted)',
              marginTop: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <QuarantineBadge tank={tank} />
          {sharedBy && (
            <span
              className="font-mono"
              style={{
                fontSize: 10,
                letterSpacing: '0.08em',
                color: 'var(--cyan)',
                textTransform: 'uppercase',
              }}
            >
              {sharedBy}
            </span>
          )}
          <StatusPill pill={pill} />
        </div>
      </div>

      {/* Four at-a-glance param tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
        }}
      >
        {stripParamsFor(tank).map((p) => (
          <ParamTile
            key={p.key}
            param={p}
            entry={breakdown[p.key]}
            spark={tank.spark?.[p.key]}
            tempUnit={getDisplayUnits(tank).temp}
          />
        ))}
      </div>

      {/* Context line: residents + active dosing */}
      {(residents || dosing) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 13,
            color: 'var(--text-muted)',
          }}
        >
          {residents && <span>{residents}</span>}
          {dosing && (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--cyan)' }}
              title={`Apex DOS dosed ~${Math.round(tank.dosing.ml_24h)} mL in the last 24 h`}
            >
              <span aria-hidden="true">●</span>
              {dosing}
            </span>
          )}
        </div>
      )}

      {/* Footer meta row: freshness + due tasks + low supplies + device */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          fontSize: 13,
          color: 'var(--text-dim)',
        }}
      >
        <span>{lastReadingLine(tank.latest_reading_ts)}</span>
        {tank.due_tasks > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--cyan)',
              fontWeight: 600,
            }}
            title={`${tank.due_tasks} task${tank.due_tasks === 1 ? '' : 's'} due or overdue on this tank`}
          >
            <span aria-hidden="true">✓</span>
            {tank.due_tasks} due
          </span>
        )}
        {tank.low_supplies > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--warn, #d6a754)',
              fontWeight: 600,
            }}
            title={`${tank.low_supplies} supply item${tank.low_supplies === 1 ? '' : 's'} running low or due for replacement`}
          >
            <span aria-hidden="true">▾</span>
            {tank.low_supplies} low
          </span>
        )}
        {tank.device && <DeviceDot device={tank.device} />}
      </div>
    </button>
  );
}

// Connection status for an automated tank (Apex / HYDROS / View 7).
// Only rendered when the tank has a claimed device — a manual-only
// tank has nothing to be "offline". Green = reporting within the
// window, amber = gone quiet.
function DeviceDot({ device }) {
  const online = !!device.online;
  const color = online ? 'var(--ok, #6cc18a)' : 'var(--warn, #d6a754)';
  const fresh = freshnessLabel(device.last_seen_at);
  // One display quiet beside a live one is not the whole tank offline.
  const some = !online && device.devices > 1 && device.quiet > 0 && device.quiet < device.devices;
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color }}
      title={
        online
          ? `Device online — last reported ${fresh}`
          : `Device hasn't reported since ${fresh} — check the controller's connection`
      }
    >
      <span
        style={{ width: 6, height: 6, borderRadius: '50%', background: color }}
        aria-hidden="true"
      />
      {online ? 'Online' : some ? `${device.quiet} of ${device.devices} quiet` : 'Offline'}
    </span>
  );
}

// SVG score ring — band-coloured arc proportional to the score, number
// centered. Mirrors the big number's tone from tankTone (neutral when the
// number only says how few tests are logged).
function ScoreRing({ score, color }) {
  const has = typeof score === 'number';
  const size = 56;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = has ? Math.max(0, Math.min(100, score)) / 100 : 0;
  const dash = circ * pct;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-border)"
          strokeWidth={stroke}
        />
        {has && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ - dash}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 700,
          color,
          letterSpacing: '-0.02em',
        }}
      >
        {has ? score : '—'}
      </div>
    </div>
  );
}

function StatusPill({ pill }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 13,
        fontWeight: 600,
        color: pill.color,
        background: pillBg(pill.band),
        padding: '3px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: '50%', background: pill.color }}
        aria-hidden="true"
      />
      {pill.label}
    </span>
  );
}

function ParamTile({ param, entry, spark, tempUnit = 'F' }) {
  const stale = !!entry?.stale;
  const status = stale ? 'stale' : statusFromEntry(entry);
  const offColor = status === 'high' || status === 'low' || status === 'off';
  const hasValue = entry && typeof entry.value === 'number';
  // Temperature is stored in °F; a Celsius keeper reads °C here like on Home.
  const isTemp = param.key === 'temp';
  const value = !hasValue
    ? '—'
    : isTemp
      ? formatTemp(entry.value, tempUnit, { withUnit: false }).replace(/\.0$/, '')
      : trim(entry.value, param.key);
  const unit = isTemp ? tempLabel(tempUnit) : param.unit;
  // Forecast hint — the score engine attaches entry.forecast to params
  // that are IN range now but trending toward an edge ("see it before
  // it happens"). Only show it when the param isn't already off/stale.
  const forecast = !offColor ? forecastHint(entry?.forecast) : null;
  // A stale value is no longer trusted as the tank's current state
  // (silent source, frozen value, or a stuck probe reading the exact
  // same number too long). Show it dimmed + struck with a warning dot
  // so the keeper doesn't read a dead number as live. The single-tank
  // Home explains the why; here it's a glanceable "don't trust this."
  const valueColor = stale ? 'var(--text-dim)' : offColor ? 'var(--warn, #d6a754)' : 'var(--text)';
  const staleTitle = !stale
    ? undefined
    : entry.stale_reason === 'test_time_unknown'
      ? `${param.label} came from your Trident, and the display can't send when it tested yet. TankSensei shows it but does not grade it. A test you log by hand counts straight away.`
      : `${param.label} hasn't updated — Sensei has stopped trusting this reading (${staleReasonLabel(entry.stale_reason)}). Log a fresh test or check the probe.`;
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}
      title={staleTitle}
    >
      <span
        className="font-heading"
        style={{
          fontSize: 10,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: stale ? 'var(--warn, #d6a754)' : 'var(--text-dim)',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {param.label}
        {stale && (
          <span aria-hidden="true" style={{ color: 'var(--warn, #d6a754)', fontSize: 11 }}>
            ⚠
          </span>
        )}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, minWidth: 0 }}>
        <span
          style={{
            fontSize: 19,
            fontWeight: 600,
            lineHeight: 1,
            color: valueColor,
            textDecoration: stale ? 'line-through' : 'none',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {hasValue && unit && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{unit}</span>}
        {!stale && forecast && (
          <span
            title={`Heading ${forecast.dirWord} — projected to cross the band in about ${forecast.days} day${forecast.days === 1 ? '' : 's'} at the current rate.`}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--warn, #d6a754)',
              letterSpacing: '0.02em',
              marginLeft: 'auto',
            }}
          >
            {forecast.arrow}
            {forecast.days}d
          </span>
        )}
      </div>
      {stale ? (
        <span style={{ fontSize: 10, color: 'var(--warn, #d6a754)', letterSpacing: '0.06em' }}>
          {staleShort(entry.stale_reason)}
        </span>
      ) : (
        <Sparkline data={spark} status={offColor ? 'off' : 'ok'} w={64} h={16} />
      )}
    </div>
  );
}

// Compact forecast badge data from a breakdown entry's `forecast`
// ({ direction: 'low'|'high', days_to_breach }). Returns { arrow,
// days, dirWord } or null. Rounds to whole days and floors at 1 so a
// 0.x-day projection still reads "1d" rather than "0d".
function forecastHint(forecast) {
  if (!forecast || typeof forecast.days_to_breach !== 'number') return null;
  const days = Math.max(1, Math.round(forecast.days_to_breach));
  const low = forecast.direction === 'low';
  return { arrow: low ? '↓' : '↑', days, dirWord: low ? 'low' : 'high' };
}

// Short stale tag shown under the struck value — tells the keeper which
// kind of "don't trust this" it is, in two words.
function staleShort(reason) {
  if (reason === 'stuck') return 'STUCK';
  if (reason === 'frozen') return 'NOT MOVING';
  if (reason === 'silent') return 'NO DATA';
  if (reason === 'test_time_unknown') return 'TEST TIME?';
  return 'STALE';
}
function staleReasonLabel(reason) {
  if (reason === 'stuck') return 'the value has been identical too long — likely a stuck probe';
  if (reason === 'frozen') return "the value hasn't moved — the tester likely stopped";
  if (reason === 'silent') return 'no new readings have arrived';
  if (reason === 'test_time_unknown')
    return 'it came from the Trident, which does not say when it tested';
  return 'no longer current';
}

function pillBg(band) {
  if (band === 'good') return 'rgba(108, 193, 138, 0.14)';
  if (band === 'watch') return 'rgba(214, 167, 84, 0.14)';
  if (band === 'attention') return 'rgba(217, 112, 112, 0.14)';
  return 'rgba(255,255,255,0.06)';
}

// Volume + type + phase + age subtitle ("120 gal · reef · Established
// 2y"). Volume already carries the keeper's display unit server-side;
// every piece is optional and omitted when missing.
function tankSubtitle(tank) {
  const bits = [];
  if (tank.volume_gallons)
    bits.push(formatVolume(tank.volume_gallons, getDisplayUnits(tank).volume));
  if (tank.type) bits.push(tank.type);
  // A QT's "Cycling" would tell the keeper ammonia is expected; it is not,
  // with a fish in there.
  const phase = isQuarantineTank(tank) ? null : phaseLabel(tank.phase);
  const age = tankAgeLabel(tank.established_date);
  if (phase && age) bits.push(`${phase} ${age}`);
  else if (phase) bits.push(phase);
  else if (age) bits.push(age);
  return bits.join(' · ') || (isFreshwaterTank(tank) ? 'Freshwater tank' : 'Reef tank');
}

// Per-param display precision — temp/pH stay at 1 decimal, calcium/
// magnesium round to whole numbers, phosphate at 2 decimals, nitrate
// at 1. Matches what reefers actually write.
function trim(n, paramKey) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (paramKey === 'calcium' || paramKey === 'magnesium') return String(Math.round(v));
  if (paramKey === 'phosphate') return v.toFixed(2);
  if (paramKey === 'nitrate') return v.toFixed(1).replace(/\.0$/, '');
  // Kits read ammonia and nitrite in steps of 0.25 or finer; one decimal
  // would show a 0.25 trace as 0.3.
  if (paramKey === 'ammonia' || paramKey === 'nitrite') return String(Number(v.toFixed(2)));
  return v.toFixed(1).replace(/\.0$/, '');
}
